import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBirthInfo } from '../../../contexts/BirthInfoContext';
import ResultsPreviewModal from './ResultsPreviewModal';
import PaymentGatewayModal from '../../../components/feature/PaymentGatewayModal';

const ZODIAC_SYMBOLS = ['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'];
const HANJA_CHARS = ['命','運','天','地','人','德','福','壽','財','官'];

interface FloatingSymbol {
  id: number;
  char: string;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
  opacity: number;
  type: 'zodiac' | 'hanja';
}

function useFloatingSymbols(count: number): FloatingSymbol[] {
  const [symbols] = useState<FloatingSymbol[]>(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      char: i % 2 === 0
        ? ZODIAC_SYMBOLS[i % ZODIAC_SYMBOLS.length]
        : HANJA_CHARS[i % HANJA_CHARS.length],
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 14 + Math.random() * 20,
      duration: 12 + Math.random() * 20,
      delay: Math.random() * 10,
      opacity: 0.06 + Math.random() * 0.12,
      type: i % 2 === 0 ? 'zodiac' : 'hanja',
    }))
  );
  return symbols;
}

type Destination = 'live-report' | 'annual-report' | 'lifetime-report';

const DESTINATIONS: { id: Destination; icon: string; title: string; sub: string; badge?: string }[] = [
  { id: 'annual-report', icon: 'ri-calendar-2-line', title: '1년 운세 리포트', sub: '월별 재물·애정·직업운', badge: '추천' },
  { id: 'live-report', icon: 'ri-eye-line', title: '사주팔자 분석', sub: '오행·십신 종합 분석' },
  { id: 'lifetime-report', icon: 'ri-infinity-line', title: '평생 운세 리포트', sub: '대운·인생 전체 사이클' },
];

const KOREAN_CITIES = [
  '서울특별시', '부산광역시', '인천광역시', '대구광역시', '대전광역시',
  '광주광역시', '울산광역시', '세종특별자치시', '수원시', '성남시',
  '안양시', '안산시', '고양시', '용인시', '청주시', '전주시',
  '천안시', '포항시', '창원시', '제주시', '경주시', '강릉시',
  '춘천시', '원주시', '목포시', '여수시', '순천시', '평택시',
  'New York, USA', 'Los Angeles, USA', 'Tokyo, Japan', 'Beijing, China',
  'Shanghai, China', 'London, UK', 'Paris, France', 'Sydney, Australia',
  'Singapore', 'Hong Kong', 'Toronto, Canada', 'Vancouver, Canada',
];

type BgTheme = 'teal' | 'purple';

const BG_THEMES: Record<BgTheme, {
  label: string;
  icon: string;
  image: string;
  overlay1: string;
  overlay2: string;
  glow1: string;
  glow2: string;
  glow3: string;
  mouseGlow: string;
}> = {
  teal: {
    label: '청록 은하수',
    icon: 'ri-moon-foggy-line',
    image: 'url(https://public.readdy.ai/ai/img_res/47113914650ddfacdb1538edd8293fce.jpg)',
    overlay1: 'from-[#050210]/40 via-[#080418]/20 to-[#050210]/60',
    overlay2: 'from-[#100030]/15 via-transparent to-[#001520]/10',
    glow1: 'bg-teal-800/8',
    glow2: 'bg-cyan-700/6',
    glow3: 'bg-violet-900/8',
    mouseGlow: 'rgba(20,255,200,0.04), rgba(139,92,246,0.05)',
  },
  purple: {
    label: '보라 성운',
    icon: 'ri-sparkling-2-line',
    image: 'url(https://readdy.ai/api/search-image?query=majestic%20deep%20space%20nebula%20with%20vivid%20magenta%20purple%20violet%20aurora%20ribbons%20swirling%20across%20pitch%20black%20cosmos%20glowing%20pink%20crimson%20wisps%20dense%20star%20fields%20brilliant%20orange%20gold%20star%20clusters%20dramatic%20cosmic%20energy%20streams%20mystical%20destiny%20fortune%20atmosphere%20otherworldly%20beautiful&width=1920&height=1080&seq=hero-galaxy-purple-nebula-v2&orientation=landscape)',
    overlay1: 'from-[#0a0018]/45 via-[#0d0025]/18 to-[#080015]/55',
    overlay2: 'from-[#1a0030]/18 via-transparent to-[#200010]/12',
    glow1: 'bg-purple-800/10',
    glow2: 'bg-pink-700/7',
    glow3: 'bg-indigo-900/10',
    mouseGlow: 'rgba(200,50,255,0.05), rgba(255,80,180,0.04)',
  },
};

export default function HeroSection() {
  const navigate = useNavigate();
  const { setBirthInfo } = useBirthInfo();
  const [formData, setFormData] = useState({
    name: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    gender: 'male' as 'male' | 'female',
  });
  const [destination, setDestination] = useState<Destination>('annual-report');
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [bgTheme, setBgTheme] = useState<BgTheme>(() => {
    return (localStorage.getItem('hero-bg-theme') as BgTheme) || 'teal';
  });
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const floatingSymbols = useFloatingSymbols(28);

  const toggleTheme = () => {
    const next: BgTheme = bgTheme === 'teal' ? 'purple' : 'teal';
    setBgTheme(next);
    localStorage.setItem('hero-bg-theme', next);
  };

  const theme = BG_THEMES[bgTheme];
  const nextTheme = BG_THEMES[bgTheme === 'teal' ? 'purple' : 'teal'];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      setMousePos({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100,
      });
    };
    const el = heroRef.current;
    el?.addEventListener('mousemove', handleMouseMove);
    return () => el?.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.birthDate || !formData.birthTime || !formData.birthPlace) return;
    setBirthInfo(formData);
    setShowPreviewModal(true);
  };

  const handlePayClick = () => {
    localStorage.setItem('destiny-report-data', JSON.stringify(formData));
    setShowPreviewModal(false);
    setShowPaymentModal(true);
  };

  const destConfig = DESTINATIONS.find((d) => d.id === destination)!;

  return (
    <>
      <div ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* ── Background Theme Toggle ── */}
        <div className="absolute top-24 right-5 z-30">
          <div
            className="relative"
            onMouseEnter={() => setShowThemePanel(true)}
            onMouseLeave={() => setShowThemePanel(false)}
          >
            {/* Toggle button */}
            <button
              onClick={toggleTheme}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 bg-black/30 backdrop-blur-md text-white/70 hover:text-white hover:border-primary-gold/50 hover:bg-black/50 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-palette-line text-sm"></i>
            </button>

            {/* Theme preview panel */}
            {showThemePanel && (
              <div className="absolute top-full right-0 mt-2 w-52 rounded-2xl bg-black/80 border border-white/10 backdrop-blur-xl overflow-hidden pointer-events-auto">
                <div className="p-2 space-y-1.5">
                  <p className="text-white/40 text-[10px] font-semibold uppercase tracking-widest px-1 pt-0.5">배경 테마 선택</p>
                  {(Object.entries(BG_THEMES) as [BgTheme, typeof BG_THEMES[BgTheme]][]).map(([key, t]) => (
                    <button
                      key={key}
                      onClick={() => { setBgTheme(key); localStorage.setItem('hero-bg-theme', key); }}
                      className={`w-full flex items-center gap-2.5 rounded-xl overflow-hidden transition-all duration-200 cursor-pointer border-2 ${
                        bgTheme === key ? 'border-primary-gold/70' : 'border-transparent hover:border-white/20'
                      }`}
                    >
                      {/* Thumbnail */}
                      <div
                        className="w-14 h-9 flex-shrink-0 bg-cover bg-center"
                        style={{ backgroundImage: t.image }}
                      />
                      <div className="flex-1 text-left pr-2">
                        <p className="text-white text-xs font-semibold">{t.label}</p>
                        <p className="text-white/40 text-[10px]">
                          {key === 'teal' ? '청록·시안 은하수' : '마젠타·보라 성운'}
                        </p>
                      </div>
                      {bgTheme === key && (
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mr-2">
                          <i className="ri-check-line text-primary-gold text-xs"></i>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Background Layers */}
        {/* 1. Base cosmic galaxy image — theme-based */}
        <div
          className="absolute inset-0 bg-cover bg-center scale-105 transition-all duration-1000"
          style={{
            backgroundImage: theme.image,
            transform: `scale(1.05) translate(${(mousePos.x - 50) * -0.03}%, ${(mousePos.y - 50) * -0.03}%)`,
            transition: 'background-image 0.8s ease, transform 0.1s ease-out',
          }}
        />

        {/* 2. Subtle overlay to darken and blend */}
        <div className={`absolute inset-0 bg-gradient-to-b ${theme.overlay1} transition-all duration-800`} />
        <div className={`absolute inset-0 bg-gradient-to-r ${theme.overlay2} transition-all duration-800`} />

        {/* 3. Animated radial glow that follows mouse */}
        <div
          className="absolute inset-0 pointer-events-none transition-all duration-700 ease-out"
          style={{
            background: `radial-gradient(ellipse 60% 50% at ${mousePos.x}% ${mousePos.y}%, ${theme.mouseGlow.split(', ')[0]} 0%, ${theme.mouseGlow.split(', ')[1]} 40%, transparent 70%)`,
          }}
        />

        {/* 4. Static ambient glows */}
        <div className={`absolute top-1/4 left-1/4 w-96 h-96 ${theme.glow1} rounded-full blur-3xl pointer-events-none`} style={{ animation: 'pulse 6s ease-in-out infinite' }} />
        <div className={`absolute bottom-1/3 right-1/4 w-80 h-80 ${theme.glow2} rounded-full blur-3xl pointer-events-none`} style={{ animation: 'pulse 8s ease-in-out infinite 2s' }} />
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] ${theme.glow3} rounded-full blur-3xl pointer-events-none`} />

        {/* 5. Floating zodiac & hanja symbols */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingSymbols.map((sym) => (
            <div
              key={sym.id}
              className="absolute select-none"
              style={{
                left: `${sym.x}%`,
                top: `${sym.y}%`,
                fontSize: `${sym.size}px`,
                opacity: sym.opacity,
                color: sym.type === 'zodiac' ? 'rgba(251,191,36,0.9)' : 'rgba(192,132,252,0.9)',
                fontFamily: sym.type === 'hanja' ? '"Noto Serif KR", serif' : 'inherit',
                animation: `floatSym ${sym.duration}s ease-in-out infinite ${sym.delay}s`,
                textShadow: sym.type === 'zodiac' ? '0 0 12px rgba(251,191,36,0.5)' : '0 0 12px rgba(192,132,252,0.5)',
              }}
            >
              {sym.char}
            </div>
          ))}
        </div>

        {/* 6. Constellation dots */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 70 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                left: `${(i * 137.5) % 100}%`,
                top: `${(i * 71.3) % 100}%`,
                width: i % 7 === 0 ? '3px' : i % 3 === 0 ? '2px' : '1px',
                height: i % 7 === 0 ? '3px' : i % 3 === 0 ? '2px' : '1px',
                opacity: 0.15 + (i % 5) * 0.1,
                animation: `twinkle ${2 + (i % 4)}s ease-in-out infinite ${(i * 0.3) % 3}s`,
                boxShadow: i % 7 === 0 ? '0 0 6px rgba(255,255,255,0.8)' : 'none',
              }}
            />
          ))}
        </div>

        {/* 7. Animated ring around the center */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className="w-[700px] h-[700px] rounded-full border border-primary-gold/5"
            style={{ animation: 'spinSlow 60s linear infinite' }}
          >
            {ZODIAC_SYMBOLS.map((sym, i) => (
              <div
                key={i}
                className="absolute text-primary-gold/20 text-sm"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: `rotate(${i * 30}deg) translateY(-350px) rotate(-${i * 30}deg)`,
                }}
              >
                {sym}
              </div>
            ))}
          </div>
        </div>

        {/* ── Content ── */}
        <div className="relative z-10 w-full max-w-2xl mx-auto px-4 sm:px-6 py-24 sm:py-32 text-center">
          {/* Title Section */}
          <div className="mb-8 sm:mb-10" style={{ animation: 'fadeInUp 0.8s ease-out both' }}>

            {/* Top badge */}
            <div className="inline-flex items-center gap-2 mb-6">
              <span className="w-1 h-1 bg-primary-gold rounded-full" style={{ animation: 'pulse 2s ease-in-out infinite' }}></span>
              AI 사주 · 점성술 · 자미두수
              <span className="w-1 h-1 bg-primary-gold rounded-full" style={{ animation: 'pulse 2s ease-in-out infinite 1s' }}></span>
            </div>

            <div className="flex items-center justify-center gap-4 sm:gap-5 mb-5">
              {/* 命 Seal Badge */}
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 group">
                {/* Outer glow ring */}
                <div
                  className="absolute -inset-2 rounded-2xl border border-primary-gold/20"
                  style={{ animation: 'spinSlow 12s linear infinite' }}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary-gold/40 to-amber-600/20 blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#1a0f2e] to-[#0f0a1e] border-2 border-primary-gold/70 group-hover:border-primary-gold transition-all duration-300"></div>
                <div className="absolute top-1.5 left-1.5 w-3 h-3 border-t-2 border-l-2 border-primary-gold/80 rounded-tl"></div>
                <div className="absolute top-1.5 right-1.5 w-3 h-3 border-t-2 border-r-2 border-primary-gold/80 rounded-tr"></div>
                <div className="absolute bottom-1.5 left-1.5 w-3 h-3 border-b-2 border-l-2 border-primary-gold/80 rounded-bl"></div>
                <div className="absolute bottom-1.5 right-1.5 w-3 h-3 border-b-2 border-r-2 border-primary-gold/80 rounded-br"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span
                    className="text-primary-gold text-3xl sm:text-4xl md:text-6xl font-bold leading-none select-none"
                    style={{ fontFamily: '"Noto Serif KR", "Noto Serif", serif', textShadow: '0 0 20px rgba(251,191,36,1), 0 0 40px rgba(251,191,36,0.5)' }}
                  >
                    命
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-start">
                <div className="flex items-baseline gap-2 sm:gap-3">
                  <h1
                    className="text-4xl sm:text-5xl md:text-6xl font-black text-white tracking-wide leading-none"
                    style={{ fontFamily: '"Cinzel", "Playfair Display", serif', textShadow: '0 0 40px rgba(255,255,255,0.2)' }}
                  >
                    DESTINY
                  </h1>
                  <span
                    className="text-xl sm:text-2xl md:text-3xl font-bold text-primary-gold tracking-[0.3em] leading-none"
                    style={{ fontFamily: '"Cinzel", monospace', textShadow: '0 0 20px rgba(251,191,36,0.8)' }}
                  >
                    GPT
                  </span>
                </div>
                <div className="h-px w-full bg-gradient-to-r from-primary-gold/0 via-primary-gold/70 to-primary-gold/0 my-1.5"></div>
                <span className="text-[9px] sm:text-[10px] tracking-[0.45em] text-purple-300/70 uppercase w-full text-center">
                  운명 분석 AI
                </span>
              </div>
            </div>

            {/* Sub headline */}
            <div style={{ animation: 'fadeInUp 0.8s ease-out 0.2s both' }}>
              <p className="text-2xl sm:text-3xl md:text-4xl text-white mb-3 font-light leading-tight">
                당신의 <span className="text-primary-gold font-semibold">운명</span>을 읽어드립니다
              </p>
              <p className="text-sm text-purple-300/80 max-w-md mx-auto">
                사주팔자 · 서양 점성술 · 자미두수로 삶의 큰 그림을 그려드립니다
              </p>
            </div>

            {/* Stats row */}
            <div
              className="flex items-center justify-center gap-6 sm:gap-10 mt-6"
              style={{ animation: 'fadeInUp 0.8s ease-out 0.4s both' }}
            >
              {[
                { value: '12만+', label: '분석 완료' },
                { value: '98%', label: '만족도' },
                { value: '3종', label: '동양·서양 결합' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-primary-gold leading-none">{stat.value}</div>
                  <div className="text-xs text-purple-400/80 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Birth Info Form */}
          <div style={{ animation: 'fadeInUp 0.8s ease-out 0.5s both' }}>
            {/* Form Heading */}
            <div className="text-center mb-5">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-primary-gold/50"></div>
                <span className="text-[10px] tracking-[0.35em] text-primary-gold/70 uppercase font-medium">AI 즉시 분석</span>
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-primary-gold/50"></div>
              </div>
              <h3 className="text-base sm:text-lg font-bold text-white leading-snug">
                탄생 정보를 입력하면{' '}
                <span className="text-primary-gold">AI가 즉시 분석</span>합니다
              </h3>
              <p className="text-xs text-purple-400/70 mt-1">사주팔자 · 점성술 · 자미두수 동시 적용</p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="rounded-3xl p-6 sm:p-10 relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(20,10,40,0.65) 0%, rgba(15,8,30,0.68) 50%, rgba(18,10,35,0.66) 100%)',
                backdropFilter: 'blur(28px)',
                WebkitBackdropFilter: 'blur(28px)',
                border: '1px solid rgba(251,191,36,0.28)',
                boxShadow: '0 0 0 1px rgba(255,255,255,0.07) inset, 0 0 60px rgba(139,92,246,0.18), 0 0 120px rgba(251,191,36,0.08), 0 8px 48px rgba(0,0,0,0.50)',
              }}
            >
              {/* Form inner glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-gold/5 via-transparent to-purple-900/15 pointer-events-none rounded-3xl" />
              {/* Top highlight line */}
              <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-primary-gold/35 to-transparent pointer-events-none" />

              <div className="relative space-y-5">
                {/* Name */}
                <div className="text-left">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-primary-gold mb-2">
                    <i className="ri-user-smile-line"></i>
                    <span>이름 <span className="text-purple-400/70 font-normal text-xs">(선택)</span></span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="예: 홍길동"
                    className="w-full px-4 py-3.5 rounded-xl bg-white/90 border border-white/40 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-gold/60 focus:border-transparent transition-all text-sm focus:bg-white"
                  />
                </div>

                {/* Birth Date */}
                <div className="text-left">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-primary-gold mb-2">
                    <i className="ri-calendar-line"></i>
                    <span>생년월일</span>
                  </label>
                  <input
                    type="date"
                    value={formData.birthDate}
                    onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                    className="w-full px-4 py-3.5 rounded-xl bg-white/90 border border-white/40 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-gold/60 focus:border-transparent transition-all text-sm focus:bg-white"
                    required
                  />
                </div>

                {/* Birth Time */}
                <div className="text-left">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-primary-gold mb-2">
                    <i className="ri-time-line"></i>
                    <span>출생 시간</span>
                  </label>
                  <input
                    type="time"
                    value={formData.birthTime}
                    onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                    className="w-full px-4 py-3.5 rounded-xl bg-white/90 border border-white/40 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-gold/60 focus:border-transparent transition-all text-sm focus:bg-white"
                    required
                  />
                </div>

                {/* Birth Place — autocomplete */}
                <div className="text-left">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-primary-gold mb-2">
                    <i className="ri-map-pin-line"></i>
                    <span>출생 장소</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      list="birth-place-list"
                      value={formData.birthPlace}
                      onChange={(e) => setFormData({ ...formData, birthPlace: e.target.value })}
                      placeholder="예: 서울특별시 (도시 입력 후 선택)"
                      className="w-full px-4 py-3.5 pr-10 rounded-xl bg-white/90 border border-white/40 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-gold/60 focus:border-transparent transition-all text-sm focus:bg-white"
                      required
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-5 h-5 flex items-center justify-center">
                      <i className="ri-search-line text-purple-400/50 text-sm"></i>
                    </div>
                  </div>
                  <datalist id="birth-place-list">
                    {KOREAN_CITIES.map((city) => (
                      <option key={city} value={city} />
                    ))}
                  </datalist>
                  <p className="text-[11px] text-purple-400/60 mt-1.5 flex items-center gap-1">
                    <i className="ri-information-line text-xs"></i>
                    시/도 단위로 입력하시면 됩니다
                  </p>
                </div>

                {/* Gender */}
                <div className="text-left">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-primary-gold mb-2">
                    <i className="ri-user-line"></i>
                    <span>성별</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {(['male', 'female'] as const).map((g) => (
                      <label key={g} className="cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          value={g}
                          checked={formData.gender === g}
                          onChange={(e) =>
                            setFormData({ ...formData, gender: e.target.value as 'male' | 'female' })
                          }
                          className="sr-only"
                        />
                        <div
                          className={`px-4 py-3.5 rounded-xl border-2 text-center text-sm font-medium transition-all duration-300 ${
                            formData.gender === g
                              ? 'bg-gradient-to-r from-primary-purple to-primary-light border-primary-gold text-white'
                              : 'bg-white/5 border-white/15 text-purple-300 hover:border-white/30 hover:bg-white/8'
                          }`}
                        >
                          {g === 'male' ? '남성' : '여성'}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Destination Selector */}
                <div className="text-left">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-primary-gold mb-2">
                    <i className="ri-route-line"></i>
                    <span>바로 이동할 페이지</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {DESTINATIONS.map((dest) => (
                      <button
                        key={dest.id}
                        type="button"
                        onClick={() => setDestination(dest.id)}
                        className={`relative flex flex-col items-center gap-1 px-2 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                          destination === dest.id
                            ? 'border-primary-gold bg-primary-gold/15 text-white'
                            : 'border-white/10 bg-white/3 text-purple-400/60 hover:border-white/20 hover:text-purple-300'
                        }`}
                      >
                        {dest.badge && destination === dest.id && (
                          <div className="absolute -top-2 -right-1">
                            <span className="text-[9px] bg-primary-gold text-black font-black px-1.5 py-0.5 rounded-full whitespace-nowrap">{dest.badge}</span>
                          </div>
                        )}
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${dest.icon} text-sm ${destination === dest.id ? 'text-primary-gold' : ''}`}></i>
                        </div>
                        <span className="text-[10px] font-bold leading-tight text-center">{dest.title}</span>
                        <span className="text-[9px] leading-tight text-center opacity-60">{dest.sub}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="relative w-full px-6 py-4 bg-gradient-to-r from-primary-gold to-amber-400 text-primary-dark font-bold rounded-xl transition-all duration-300 transform hover:scale-[1.02] whitespace-nowrap text-base mt-2 flex items-center justify-center space-x-2 overflow-hidden group/btn cursor-pointer"
                  style={{ boxShadow: '0 0 30px rgba(251,191,36,0.3)' }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-300 to-primary-gold opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300" />
                  <span className="relative">
                    {destConfig.title}으로 바로 시작
                  </span>
                  <i className="ri-arrow-right-line relative"></i>
                </button>

                {/* Conversion boosters below button */}
                <div className="flex items-center justify-center gap-4 pt-1">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30">
                    <i className="ri-gift-2-line text-emerald-400 text-xs"></i>
                    <span className="text-xs font-semibold text-emerald-400">첫 분석 무료</span>
                  </div>
                  <span className="text-purple-400/30 text-sm">|</span>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-flashlight-line text-primary-gold/70 text-xs"></i>
                    <span className="text-xs text-purple-300/70">평균 <span className="text-white/80 font-semibold">3초</span> 내 결과</span>
                  </div>
                  <span className="text-purple-400/30 text-sm">|</span>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-shield-check-line text-purple-400/60 text-xs"></i>
                    <span className="text-xs text-purple-300/70">개인정보 보호</span>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Privacy Notice */}
          <p
            className="mt-6 text-xs sm:text-sm text-purple-300/70"
            style={{ animation: 'fadeInUp 0.8s ease-out 0.7s both' }}
          >
            <i className="ri-shield-check-line mr-1 text-primary-gold/60"></i>
            입력하신 정보는 안전하게 보호되며 운세 분석에만 사용됩니다
          </p>
        </div>

        {/* Keyframe styles */}
        <style>{`
          @keyframes floatSym {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(-18px) rotate(5deg); }
            50% { transform: translateY(-8px) rotate(-3deg); }
            75% { transform: translateY(-24px) rotate(4deg); }
          }
          @keyframes twinkle {
            0%, 100% { opacity: 0.15; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.6); }
          }
          @keyframes spinSlow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(28px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>

      {/* Modals rendered outside the hero to avoid z-index clipping */}
      <ResultsPreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          navigate('/' + destination);
        }}
        formData={formData}
        onPayClick={handlePayClick}
      />

      <PaymentGatewayModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        price={14900}
        productName="전체 운명 분석 리포트"
      />
    </>
  );
}
